import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [f, setF] = useState({ next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { data, error: linkError, isLoading } = useQuery({ queryKey: ["setup", token], queryFn: async () => (await api.get(`/auth/setup-password/${encodeURIComponent(token)}`)).data, retry: false, enabled: !!token });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.next.length < 8) return setError("Use at least 8 characters");
    if (f.next !== f.confirm) return setError("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/setup-password", { token, password: f.next });
      toast.success("Password saved. You can sign in now.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const bad = !token || linkError;
  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-16">
      {isLoading ? <p className="text-muted-foreground">Checking your link…</p> : bad ? (
        <div data-testid="set-password-invalid" className="fade-up max-w-sm space-y-4 text-center">
          <h1 className="font-heading text-2xl font-black tracking-tight">This link does not work</h1>
          <p className="text-muted-foreground">{linkError ? formatApiError(linkError) : "The link is missing its code."}</p>
          <Link to="/login" className="inline-block font-semibold text-brand-primary underline">Go to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="set-password-form" className="fade-up w-full max-w-sm space-y-5">
          <div>
            <div className="overline-label">Welcome</div>
            <h1 className="mt-2 font-heading text-3xl font-black tracking-tight">Choose your password</h1>
            <p className="mt-2 text-sm text-muted-foreground">You will sign in as <strong translate="no" data-testid="set-password-email">{data.email}</strong>. Use 8 or more characters.</p>
          </div>
          {[["set-password-new", "New password", "next"], ["set-password-confirm", "Confirm password", "confirm"]].map(([id, label, k]) => (
            <div key={id} className="space-y-2">
              <Label htmlFor={id}>{label}</Label>
              <Input id={id} data-testid={`${id}-input`} type={show ? "text" : "password"} required autoComplete="new-password" className="h-11 rounded-sm" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
            </div>
          ))}
          <button type="button" data-testid="set-password-toggle" onClick={() => setShow(!show)} className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground">
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}{show ? "Hide passwords" : "Show passwords"}
          </button>
          {error && <p data-testid="set-password-error" role="alert" className="text-sm text-destructive">{error}</p>}
          <button type="submit" disabled={busy} data-testid="set-password-submit" className="h-11 w-full rounded-sm bg-brand-primary text-sm font-semibold text-brand-primary-fg hover:opacity-90 disabled:opacity-60">{busy ? "Saving…" : "Save password"}</button>
        </form>
      )}
    </div>
  );
}
